import { useState } from 'react'
import { QrCode } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { MobileBarcodeScannerDialog } from '@/components/orders/mobile-barcode-scanner-dialog'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /**
     * Extracts a lot id (uuid) from the QR text. Return null when the QR is
     * not a lot label for this lot type, so the button can toast a helpful
     * error and keep the camera open (continuous mode).
     */
    parseLotId: (rawText: string) => string | null
    onScanned: (lotId: string) => void
    label?: string
    className?: string
}

/**
 * Reusable "Quét mã lô" button — opens a QR scanner dialog, decodes the
 * printed tem A7 QR (which encodes `<baseURL>/.../lots/<uuid>`), and calls
 * the parent with the extracted lot id. Parent typically calls
 * `append({...})` from react-hook-form's useFieldArray.
 */
export function ScanLotCodeButton({
    parseLotId,
    onScanned,
    label,
    className,
}: Props) {
    const btnLabel = label ?? m.lot_scan_btn_label()
    const [open, setOpen] = useState(false)
    return (
        <>
            <Button
                variant="outline"
                size="sm"
                className={className}
                onClick={() => setOpen(true)}
            >
                <QrCode className="mr-1 h-4 w-4" /> {btnLabel}
            </Button>
            {open && (
                <MobileBarcodeScannerDialog
                    open={open}
                    onOpenChange={setOpen}
                    formats="qr"
                    continuous={false}
                    onRawScan={(raw) => {
                        const lotId = parseLotId(raw)
                        if (!lotId) {
                            toast.error(m.lot_scan_invalid_qr())
                            return
                        }
                        onScanned(lotId)
                        toast.success(m.lot_scan_added_toast())
                        setOpen(false)
                    }}
                />
            )}
        </>
    )
}

/** Regex used to extract the lot uuid from a scanned URL. Matches any of the
 *  5 lot-type paths from the tem A7 label QR encoded by the BE renderer. */
const LOT_URL_RE =
    /\/(?:purchase-lots\/lots|sorting-warehouse\/lots|scenting-warehouse\/lots|packaging-warehouse\/lots|sales-warehouse\/lots)\/([0-9a-f-]{36})/i

/** Parses any lot URL and returns the uuid, or null if none matched. Use in
 *  ScanLotCodeButton.parseLotId. */
export function parseAnyLotIdFromQR(raw: string): string | null {
    const m = raw.trim().match(LOT_URL_RE)
    return m ? m[1] : null
}

/** Parses a specific lot type. Use this when the PXK new page only accepts
 *  one lot type, so scanning a wrong-type tem toasts an error. */
export function makeLotIdParser(
    kind:
        | 'purchase-lots/lots'
        | 'sorting-warehouse/lots'
        | 'scenting-warehouse/lots'
        | 'packaging-warehouse/lots'
        | 'sales-warehouse/lots',
): (raw: string) => string | null {
    const re = new RegExp(`/${kind.replace(/\//g, '\\/')}/([0-9a-f-]{36})`, 'i')
    return (raw: string) => {
        const m = raw.trim().match(re)
        return m ? m[1] : null
    }
}
